import { useEffect } from 'react';
import { Routes, Route, Outlet, useLocation } from 'react-router-dom';
import { NotFound } from '@acast-tech/product-suite-app-shell';
import { RouterErrorBoundary, PageWrapper } from './components';
import PageErrorBoundary from './components/PageWrapper/PageErrorBoundary';
import {
  AudiencePage,
  ContentPerformancePageEcho,
  EarningsPageEcho,
  LegacyContentPerformanceRedirect,
  PayoutsPage,
  SinglePayoutPage,
} from './routes';
import useAutoReloadWhenInactive from './hooks/useAutoReloadWhenInactive';
import { APP_VERSION, initializeVersionCleanup } from './utils/versionUtils';
import { MixpanelIdentifier, MixpanelLoader } from './utils/mixpanelUtils/mixpanelLoader';
import { config } from './config';
import { useEchoDesign } from './hooks';
import AudiencePageEcho from './routes/AudiencePage/AudiencePageEcho';

/**
 * Trackers are used to track the user's actions and send them to Mixpanel.
 * @returns {JSX.Element}
 */
function Trackers() {
  return (
    <>
      <MixpanelLoader projectToken={config.mixpanelConfig.projectToken} />
      <Outlet />
    </>
  );
}

/**
 * Resets the page-level error boundary on each navigation so a stale 403/500
 * doesn't persist when the user switches routes.
 */
const PageBoundary = () => {
  const { pathname } = useLocation();
  return (
    <PageErrorBoundary key={pathname}>
      <Outlet />
    </PageErrorBoundary>
  );
};

/**
 * AuthenticatedScope is used to track the user's actions and send them to Mixpanel.
 * @returns {JSX.Element}
 */
function AuthenticatedScope() {
  return (
    <>
      <MixpanelIdentifier />
      <Outlet />
    </>
  );
}

function App() {
  useAutoReloadWhenInactive();

  useEffect(() => {
    if (process.env.NODE_ENV !== 'test') {
      console.log(`🚀 Revenue Dashboard v${APP_VERSION}`);
    }

    initializeVersionCleanup();
  }, []);

  const isEchoDesignEnabled = useEchoDesign();

  return (
    <>
      <Routes>
        <Route element={<Trackers />}>
          <Route element={<PageBoundary />}>
            <Route path="/" element={<PageWrapper />} />
            <Route
              path="content-performance"
              errorElement={<RouterErrorBoundary />}
              element={<PageWrapper networkSource="query" />}
            >
              <Route element={<AuthenticatedScope />}>
                <Route index element={<ContentPerformancePageEcho />} />
              </Route>
            </Route>
            <Route
              path=":networkId"
              errorElement={<RouterErrorBoundary />}
              element={<PageWrapper />}
            >
              <Route element={<AuthenticatedScope />}>
                <Route
                  path="content-performance/:showId?/:from?/:to?"
                  element={<LegacyContentPerformanceRedirect />}
                />
                <Route
                  path="audience/:showId?/:from?/:to?"
                  element={isEchoDesignEnabled ? <AudiencePageEcho /> : <AudiencePage />}
                />
                <Route path="earnings/:showId?/:from?/:to?" element={<EarningsPageEcho />} />
                <Route path="payouts/:year?" element={<PayoutsPage />} />
                <Route path="payouts/:year/:month" element={<SinglePayoutPage />} />
              </Route>
            </Route>
          </Route>
          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
    </>
  );
}

export default App;
